import * as React from 'react';

import { Cue, CueExplanation } from './index';

import { ReadAloudButton } from '../readAloud/readAloudButton';
import { useReadAloud } from '../readAloud/readAloudContext';
import { buildWordTimeline, cueSegmentIndexForWord, cueSpeechSegments, cuesToSpeechText, wordIndexForCharIndex } from '../../libs/speech/speechText';
import { ENGLISH } from '../../utils/languageList';
import { SMALL_ICON_BASE_SRC } from '../../utils/constants';

const arrow = `${SMALL_ICON_BASE_SRC}/pointing-arrow-left.svg`;

interface CuesProps {
  diagnosticID?: string;
  displayArrowAndText: boolean;
  includeReadAloudButton?: boolean;
  language?: string;
  languageLocale?: string;
  question: any;
  translate?: (key: any, opts?: any) => any;
}

const translateCueLabel = (question: any, translate: CuesProps['translate']) => {
  const { cues, cuesLabel } = question;

  if (cues && cuesLabel && translate) {
    const text = `cues^${cuesLabel}`;
    return translate(text);
  } else if (translate) {
    return translate('cues^joining word');
  }

  return 'joining word';
};

const getJoiningWordsText = (props: CuesProps) => {
  const { language, question, translate } = props;

  if (language && language !== ENGLISH && translate) {
    return translateCueLabel(question, translate);
  } else if (question.cues && question.cuesLabel) {
    return question.cuesLabel;
  } else if (question.cues && question.cues.length === 1) {
    return 'joining word';
  } else {
    return 'joining words';
  }
};

export const Cues: React.FC<CuesProps> = (props) => {
  const { displayArrowAndText, includeReadAloudButton, languageLocale, question } = props;
  const { cues, cuesLabel } = question;
  const { readAloudEnabled } = useReadAloud();
  const [spokenSegmentIndex, setSpokenSegmentIndex] = React.useState<number | null>(null);
  const explanationId = React.useId();

  if (!cues || cues.length === 0 || cues[0] === '') {
    return <span />;
  }

  const text = getJoiningWordsText(props);
  const showReadAloudButton = includeReadAloudButton && readAloudEnabled;
  const segments = showReadAloudButton ? cueSpeechSegments(cues, cuesLabel) : [];
  const speechText = showReadAloudButton ? cuesToSpeechText(cues, cuesLabel) : '';
  const timeline = buildWordTimeline(speechText);
  const handleBoundary = (charIndex: number) => {
    const wordIndex = wordIndexForCharIndex(timeline, charIndex);
    setSpokenSegmentIndex(wordIndex === null ? null : cueSegmentIndexForWord(segments, wordIndex));
  };
  const handleSpeechEnd = () => setSpokenSegmentIndex(null);
  const spokenCueIndex = spokenSegmentIndex === null ? null : segments[spokenSegmentIndex]?.cueIndex ?? null;
  const labelHighlighted = spokenSegmentIndex === 0;

  return (
    <div className={showReadAloudButton ? 'cues cues-with-read-aloud' : 'cues'} lang={languageLocale}>
      <ul
        aria-labelledby={displayArrowAndText ? explanationId : undefined}
        className="cues-list"
      >
        {cues.map((cue: string, i: number) => {
          const isLast = i === cues.length - 1;

          return (
            <li className="cues-item" key={`${i}${cue}`}>
              <Cue cue={cue} highlighted={spokenCueIndex === i} />

              {isLast && displayArrowAndText ? (
                <span className="cues-suffix">
                  {cuesLabel !== ' ' ? (
                    <img
                      alt=""
                      aria-hidden="true"
                      className="cues-arrow"
                      src={arrow}
                    />
                  ) : null}

                  <span className={labelHighlighted ? 'cues-label karaoke-highlight' : 'cues-label'} id={explanationId}>
                    <CueExplanation languageLocale={languageLocale} text={text} />
                  </span>
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {showReadAloudButton && <ReadAloudButton controlsId={explanationId} label="Read the joining words aloud" onBoundary={handleBoundary} onSpeechEnd={handleSpeechEnd} text={speechText} />}
    </div>
  );
};

export default Cues;
